/*
 * Generates the table of tickets from data retrieved from the API.
 * Mostly taken from the original STV app with the following changes:
 * - Some properties renamed to match the new API
 * - Rather than generating the ticket URL here, we just retrieve it from the API
 */

import React from "react";
import moment from "moment-timezone";
import { Ticket } from "../pages/TicketsPage";
import {
  dateDisplay,
  personDisplay,
  companyDisplay,
  agentDisplay,
  ticketTypeDisplay,
  DATE_FORMAT,
} from "../helpers/dataHelper";
import MUIDataTable from "mui-datatables";
import clsx from "clsx";
import { Chip, Typography } from "@material-ui/core";
import useRowStyles from "../hooks/useRowStyles";
import { ClientLabel } from "../helpers/interfaceHelper";
import { makeStyles } from "@material-ui/styles";

interface TicketsTableProps {
  tickets: Ticket[];
  paging: {
    page: number;
    perPage: number;
    total: number;
    setRowsPerPage: (numberOfRows: number) => void;
    setPage: (page: number) => void;
  };
  sorting: {
    sortProperty: string;
    sortDirection: number;
    setSort: (property: string) => void;
  };
  labelsEnabled: boolean;
  clientLabels: ClientLabel[];
}

const useStyles = makeStyles({
  chipLabel: {
    fontSize: "0.7rem",
    height: "1.3rem",
  },
  chipLabelUrgentPriority: {
    fontSize: "0.8rem",
    height: "1.4rem",
    fontWeight: "bolder",
    color: "tomato",
  },
});

const TicketsTable: React.FC<TicketsTableProps> = ({
  tickets,
  paging,
  sorting,
  labelsEnabled,
  clientLabels,
}) => {
  const rowClasses = useRowStyles();
  const classes = useStyles();

  const columns = [
    {
      label: "Ticket ID",
      name: "id",
      options: {
        customHeadLabelRender: (columnMeta: any) => {
          return (
            <Typography component={"span"} noWrap={true} variant="body2">
              {columnMeta.label}
            </Typography>
          );
        },
        customBodyRenderLite: (dataIndex: number) => {
          const ticket = tickets[dataIndex];
          return (
            <>
              {ticketTypeDisplay(ticket.type)}-{ticket.id}
            </>
          );
        },
      },
    },
    {
      label: "Company Name",
      name: "client",
      options: {
        customBodyRenderLite: (dataIndex: number) => {
          const ticket = tickets[dataIndex];
          let label: string | null =
            clientLabels.find((cl) => cl.client === ticket.client)?.label ??
            null;

          if (ticket.teamLabel) {
            label ? (label += " | ") : (label = "");
            label += ticket.teamLabel;
          }

          if (ticket.quickWin == "Yes") {
            label ? (label += " | ") : (label = "");
            label += "Quick Win";
          }

          return (
            <>
              {companyDisplay(ticket.client)}{" "}
              {labelsEnabled && label && (
                <Chip
                  label={label}
                  color="default"
                  size="small"
                  className={classes.chipLabel}
                />
              )}
            </>
          );
        },
      },
    },
    {
      label: "Subject",
      name: "subject",
    },
    {
      label: "From",
      name: "submitter",
      options: {
        customBodyRender: (value: any) => {
          return personDisplay(value);
        },
      },
    },
    {
      label: "Status",
      name: "status",
    },
    {
      label: "Priority",
      name: "priority",
      sortProperty: "priority",
      options: {
        customBodyRender: (value: "Urgent") => {
          if (value === "Urgent") {
            return (
              <>
                <Typography
                  component={"span"}
                  noWrap={true}
                  variant="body2"
                  align="center"
                >
                  {agentDisplay(value)}{" "}
                  <Chip
                    label="!"
                    size="small"
                    className={classes.chipLabelUrgentPriority}
                  />
                </Typography>
              </>
            );
          } else {
            return <label>{value}</label>;
          }
        },
      },
    },
    {
      label: "Agent",
      name: "agent",
      options: {
        customBodyRender: (value: any) => {
          return (
            <Typography component={"span"} noWrap={true} variant="body2">
              {agentDisplay(value)}
            </Typography>
          );
        },
      },
    },
    {
      label: "Group",
      name: "assignedGroup",
    },
    {
      label: "Created Date",
      name: "dateCreated",
      options: {
        customBodyRender: (value: any) => dateDisplay(value),
      },
    },
    {
      label: "Due Date",
      name: "dateDue",
      options: {
        customBodyRender: (value: any) => dateDisplay(value),
      },
    },
    {
      label: "Last Updated",
      name: "dateUpdated",
      options: {
        customBodyRender: (value: any) => dateDisplay(value),
      },
    },
    {
      label: "URL",
      name: "url",
      options: {
        display: false,
      },
    },
    {
      label: "Breached",
      name: "breached",
      options: {
        display: false,
      },
    },
  ];

  return (
    <MUIDataTable
      title=""
      data={tickets}
      columns={columns}
      options={{
        sortOrder: {
          name: sorting.sortProperty,
          direction: sorting.sortDirection === -1 ? "desc" : "asc",
        },
        viewColumns: false,
        print: false,
        filter: false,
        search: false,
        selectableRows: "none",
        serverSide: true,
        page: paging.page,
        rowsPerPage: paging.perPage ?? 30,
        count: paging.total,
        download: false,
        onColumnSortChange: (changedColumn: string) => {
          sorting.setSort(changedColumn);
        },
        onChangeRowsPerPage: (numberOfRows: number) => {
          paging.setRowsPerPage(numberOfRows);
        },
        onChangePage: (currentPage: number) => {
          paging.setPage(currentPage);
        },
        setTableProps: () => {
          return {
            size: "small",
          };
        },
        setRowProps: (row: any[]) => {
          const dueDate = row[9];
          const breached = row[12];
          const status: string = row[4];

          const overdueStatus = () => {
            // The dueDate is taken from the formatted string displayed in the table.
            // This string has already been converted from UTC to the local timezone,
            // therefore we don't use moment.utc here.
            const timeToDueDate = moment(dueDate, DATE_FORMAT).diff(moment());

            if (breached) {
              return "overdue";
            } else if (status.startsWith("On hold")) {
              return "ok";
            } else {
              if (timeToDueDate < 0) {
                return "overdue";
              } else if (timeToDueDate < 7200000) {
                // 2 hours
                return "close";
              } else {
                return "ok";
              }
            }
          };

          return {
            className: clsx(
              overdueStatus() === "overdue" && rowClasses.overdue,
              overdueStatus() === "close" && rowClasses.close,
              overdueStatus() === "ok" && rowClasses.standard
            ),
          };
        },
        onRowClick: (rowData: string[]) => {
          window.open(`${rowData[11]}`);
        },
        rowsPerPageOptions: [30, 100, 300],
      }}
    />
  );
};

export default TicketsTable;
